import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Trash2,
  Filter,
  ClipboardList,
  FileText,
  Receipt,
  AlertTriangle,
  Info,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { tr } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, unread, read
  const [typeFilter, setTypeFilter] = useState("all");

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const unreadOnly = filter === "unread";
      const response = await axios.get(
        `${API}/notifications?limit=100${unreadOnly ? '&unread_only=true' : ''}`,
        { withCredentials: true }
      );
      let notifs = response.data.notifications || [];
      
      // Apply read filter locally for "read" option
      if (filter === "read") {
        notifs = notifs.filter(n => n.is_read);
      }
      
      // Apply type filter
      if (typeFilter !== "all") {
        notifs = notifs.filter(n => n.notification_type === typeFilter);
      }
      
      setNotifications(notifs);
    } catch (error) {
      toast.error("Bildirimler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success("Tüm bildirimler okundu işaretlendi");
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, { withCredentials: true });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      toast.success("Bildirim silindi");
    } catch (error) {
      toast.error("Bildirim silinemedi");
    }
  };

  // Delete all notifications
  const deleteAllNotifications = async () => {
    if (!window.confirm("Tüm bildirimler silinecek. Emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/notifications`, { withCredentials: true });
      setNotifications([]);
      toast.success("Tüm bildirimler silindi");
    } catch (error) {
      toast.error("Bildirimler silinemedi");
    }
  };

  // Handle notification click
  const handleClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.notification_id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Get icon for notification type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'audit_assigned':
        return <ClipboardList className="h-5 w-5 text-blue-500" />;
      case 'report_submitted':
        return <FileText className="h-5 w-5 text-yellow-500" />;
      case 'report_approved':
        return <Check className="h-5 w-5 text-green-500" />;
      case 'report_rejected':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'expense_approved':
        return <Receipt className="h-5 w-5 text-green-500" />;
      case 'expense_rejected':
        return <Receipt className="h-5 w-5 text-red-500" />;
      case 'overdue_report':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      default:
        return <Info className="h-5 w-5 text-gray-500" />;
    }
  };

  // Get type label
  const getTypeLabel = (type) => {
    const labels = {
      'audit_assigned': 'Denetim Atama',
      'report_submitted': 'Rapor Gönderimi',
      'report_approved': 'Rapor Onayı',
      'report_rejected': 'Rapor Reddi',
      'expense_approved': 'Masraf Onayı',
      'expense_rejected': 'Masraf Reddi',
      'overdue_report': 'Gecikmiş Rapor',
      'system': 'Sistem',
      'audit_status_changed': 'Durum Değişikliği',
    };
    return labels[type] || type;
  };

  // Format time
  const formatTime = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return formatDistanceToNow(date, { addSuffix: true, locale: tr });
    } catch {
      return '';
    }
  };

  // Format full date
  const formatFullDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, "d MMMM yyyy, HH:mm", { locale: tr });
    } catch {
      return '';
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [filter, typeFilter]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Info banner */}
      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-amber-700">
          Bildirimler 7 gün sonra otomatik silinir. Okunmamış bildirimler 30 gün saklanır.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Bildirimler</h1>
          <p className="text-sm text-gray-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} okunmamış bildirim` : 'Tüm bildirimler okunmuş'}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[140px]" data-testid="filter-select">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filtre" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="unread">Okunmamış</SelectItem>
              <SelectItem value="read">Okunmuş</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[160px]" data-testid="type-filter-select">
              <SelectValue placeholder="Tür" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Türler</SelectItem>
              <SelectItem value="audit_assigned">Denetim Atama</SelectItem>
              <SelectItem value="report_submitted">Rapor Gönderimi</SelectItem>
              <SelectItem value="report_approved">Rapor Onayı</SelectItem>
              <SelectItem value="report_rejected">Rapor Reddi</SelectItem>
              <SelectItem value="expense_approved">Masraf Onayı</SelectItem>
              <SelectItem value="expense_rejected">Masraf Reddi</SelectItem>
              <SelectItem value="overdue_report">Gecikmiş Rapor</SelectItem>
            </SelectContent>
          </Select>
          
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={markAllAsRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Tümünü Okundu İşaretle
            </Button>
          )}
          
          {notifications.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={deleteAllNotifications}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="delete-all-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Tümünü Sil
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Tüm Bildirimler
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-500">
              <BellOff className="h-12 w-12 mb-3 opacity-50" />
              <p className="text-lg font-medium">Bildirim bulunamadı</p>
              <p className="text-sm">Yeni bildirimler burada görünecek</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.notification_id}
                  className={`py-4 px-3 -mx-3 cursor-pointer transition-colors hover:bg-gray-50 rounded-lg ${
                    !notification.is_read ? 'bg-blue-50/50' : ''
                  }`}
                  data-testid={`notification-${notification.notification_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getTypeIcon(notification.notification_type)}
                    </div>
                    
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleClick(notification)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <p className={`text-sm ${!notification.is_read ? 'font-semibold' : 'font-medium'} text-[#111827]`}>
                          {notification.title}
                        </p>
                        <Badge variant="secondary" className="text-xs">
                          {getTypeLabel(notification.notification_type)}
                        </Badge>
                        {!notification.is_read && (
                          <span className="w-2 h-2 rounded-full bg-blue-500" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span title={formatFullDate(notification.created_at)}>
                          {formatTime(notification.created_at)}
                        </span>
                        {notification.link && (
                          <span className="text-[#1A4D2E] hover:underline">
                            Detayları Görüntüle →
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!notification.is_read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.notification_id);
                          }}
                          title="Okundu işaretle"
                          data-testid={`mark-read-${notification.notification_id}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.notification_id);
                        }}
                        title="Sil"
                        data-testid={`delete-${notification.notification_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;
